import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { runProjection, fetchMarketSnapshot } from "@/lib/api";
import { encodePlan } from "@/lib/shareUrl";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { fmtCurrency, fmtCurrencyFull, fmtPct } from "@/lib/format";
import { Loader2, Play, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSEO from "@/lib/useSEO";

// Minimalist embeddable calculator — no nav, no extras
const DEFAULTS = {
  current_age: 32,
  retirement_age: 65,
  life_expectancy: 100,
  current_savings: 50000,
  monthly_contribution: 1500,
  annual_withdrawal: 60000,
  inflation_rate: 2.5,
  allocation: { stocks: 70, bonds: 25, cash: 5 },
  simulations: 1000,
  withdrawal_strategy: "fixed_real",
};

const Field = ({ id, label, value, onChange, prefix, suffix, step = 1 }) => (
  <div>
    <Label htmlFor={id} className="text-[10px] tracking-[0.18em] uppercase text-[#5C6B62] font-semibold">
      {label}
    </Label>
    <div className="mt-1.5 flex items-center bg-white border border-[#E5E2DC] rounded-md">
      {prefix && <span className="pl-2 text-[11px] text-[#5C6B62]">{prefix}</span>}
      <Input
        id={id}
        data-testid={`embed-${id}`}
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="border-0 bg-transparent focus-visible:ring-0 text-[#1C2B23] font-mono-num text-sm h-8"
      />
      {suffix && <span className="pr-2 text-[11px] text-[#5C6B62]">{suffix}</span>}
    </div>
  </div>
);

export default function Embed() {
  useSEO({
    title: "Embed the Free Retirement Calculator on Your Site · Centennial",
    description: "One-line iframe widget. Bring a 100-year Monte Carlo retirement calculator to your blog or advisor site in 30 seconds.",
    canonicalPath: "/embed",
  });
  const [searchParams] = useSearchParams();
  const brand = searchParams.get("brand") || "Centennial";
  const logo = searchParams.get("logo") || "";
  const color = searchParams.get("color") || "#2B5339";
  const hidePoweredBy = searchParams.get("hide_powered") === "1";
  const [inputs, setInputs] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [marketLoaded, setMarketLoaded] = useState(false);

  useEffect(() => {
    fetchMarketSnapshot().finally(() => setMarketLoaded(true));
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await runProjection(inputs);
      setResult(r);
    } catch (e) {
      console.error(e);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (marketLoaded) run();
    // eslint-disable-next-line
  }, [marketLoaded]);

  const chartData = result
    ? result.monte_carlo.map((m, i) => ({
        age: m.age,
        p50: m.p50,
        p10: m.p10,
        p90: m.p90,
        simple: result.simple[i]?.balance ?? null,
      }))
    : [];

  const viewFullUrl = useMemo(() => `/#p=${encodePlan(inputs)}`, [inputs]);

  return (
    <div data-testid="embed-page" className="min-h-screen bg-white text-[#1C2B23] font-[Manrope]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-0 h-full min-h-screen">
        {/* Form column */}
        <aside className="md:col-span-4 lg:col-span-3 p-5 sm:p-6 border-r border-[#E5E2DC] bg-[#F9F8F6]">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold">
            free retirement calc
          </div>
          <div className="flex items-center gap-2 mt-1 mb-5">
            {logo && (
              <img
                src={logo}
                alt={brand}
                className="w-7 h-7 rounded-full object-cover border border-[#E5E2DC]"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <h1 className="font-display text-2xl tracking-tight">
              {brand} <span style={{ color }}>·</span> Plan to 100.
            </h1>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2.5">
              <Field id="age" label="Age" value={inputs.current_age} onChange={(v) => setInputs({ ...inputs, current_age: v })} suffix="yrs" />
              <Field id="retire" label="Retire at" value={inputs.retirement_age} onChange={(v) => setInputs({ ...inputs, retirement_age: v })} suffix="yrs" />
            </div>
            <Field id="savings" label="Current savings" value={inputs.current_savings} onChange={(v) => setInputs({ ...inputs, current_savings: v })} prefix="$" step={1000} />
            <Field id="contrib" label="Monthly contribution" value={inputs.monthly_contribution} onChange={(v) => setInputs({ ...inputs, monthly_contribution: v })} prefix="$" step={50} />
            <Field id="withdraw" label="Annual withdrawal" value={inputs.annual_withdrawal} onChange={(v) => setInputs({ ...inputs, annual_withdrawal: v })} prefix="$" step={1000} />
          </div>
          <button
            data-testid="embed-run"
            type="button"
            onClick={run}
            disabled={running}
            className="mt-5 w-full text-white hover:opacity-90 disabled:opacity-60 rounded-full py-2.5 text-sm inline-flex items-center justify-center gap-2"
            style={{ background: color }}
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {running ? "Calculating…" : "Recalculate"}
          </button>
          <a
            href={viewFullUrl}
            target="_top"
            data-testid="embed-open-full"
            className="mt-3 w-full bg-transparent border border-[#E5E2DC] text-[#5C6B62] hover:text-[#2B5339] hover:border-[#2B5339] rounded-full py-2 text-[11px] inline-flex items-center justify-center gap-2"
          >
            View on Centennial <ExternalLink size={11} />
          </a>
        </aside>

        {/* Chart + summary column */}
        <main className="md:col-span-8 lg:col-span-9 p-5 sm:p-8">
          {result ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="border border-[#E5E2DC] rounded-lg p-3">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#5C6B62] font-semibold">success</div>
                  <div className="font-display text-2xl font-mono-num text-[#2B5339] mt-1">
                    {fmtPct(result.summary.success_probability_pct, 1)}
                  </div>
                </div>
                <div className="border border-[#E5E2DC] rounded-lg p-3">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#5C6B62] font-semibold">median final</div>
                  <div className="font-display text-xl font-mono-num text-[#1C2B23] mt-1">
                    {fmtCurrencyFull(result.summary.median_final_balance)}
                  </div>
                </div>
                <div className="border border-[#E5E2DC] rounded-lg p-3">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#5C6B62] font-semibold">p10 — p90</div>
                  <div className="font-mono-num text-xs text-[#5C6B62] mt-2">
                    {fmtCurrency(result.summary.p10_final_balance)} — {fmtCurrency(result.summary.p90_final_balance)}
                  </div>
                </div>
              </div>
              <div className="h-[360px]" style={{ minHeight: 280 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <ComposedChart data={chartData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="emb_band" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#728A7C" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#728A7C" stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis dataKey="age" tick={{ fill: "#5C6B62", fontSize: 11 }} axisLine={{ stroke: "#E5E2DC" }} tickLine={false} />
                    <YAxis tickFormatter={(v) => fmtCurrency(v)} tick={{ fill: "#5C6B62", fontSize: 11 }} axisLine={false} tickLine={false} width={60} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 10, fontSize: 11 }}
                      formatter={(v, name) => [fmtCurrencyFull(v), name]}
                      labelFormatter={(l) => `age ${l}`}
                    />
                    <ReferenceLine x={inputs.retirement_age} stroke="#C26747" strokeDasharray="4 4" />
                    <Area dataKey="p90" stroke="none" fill="url(#emb_band)" isAnimationActive={false} />
                    <Area dataKey="p10" stroke="none" fill="#ffffff" isAnimationActive={false} />
                    <Line type="monotone" dataKey="p50" stroke="#2B5339" strokeWidth={2.2} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="simple" stroke="#C26747" strokeWidth={1.6} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-[#5C6B62] text-sm">
              <Loader2 className="animate-spin mr-2" size={14} /> running first projection…
            </div>
          )}
          <div className="mt-4 flex items-center justify-between text-[10px] text-[#5C6B62]">
            {!hidePoweredBy ? (
              <span>powered by <a href={viewFullUrl} target="_top" className="text-[#2B5339] font-medium hover:underline">Centennial →</a></span>
            ) : (
              <span />
            )}
            <span>not financial advice</span>
          </div>
        </main>
      </div>
    </div>
  );
}
